/**
 * CourierDeliveryListPage — Futaros kezbesitesek listaja
 * Epic 71: Futaros Kezbesites
 *
 * Statusz szuro, futaratas/kezbesites muveletek.
 */

import type { CourierDeliveryStatus } from '@/api/courier-delivery';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useCourierDeliveries,
  useHandoffToCourier,
  useMarkDelivered,
} from '@/hooks/useCourierDelivery';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STATUS_LABEL_KEYS: Record<string, string> = {
  REQUESTED: 'courier.statusLabels.requested',
  PAYMENT_PENDING: 'courier.statusLabels.paymentPending',
  PAID: 'courier.statusLabels.paid',
  DISPATCHED: 'courier.statusLabels.dispatched',
  DELIVERED: 'courier.statusLabels.delivered',
  CANCELLED: 'courier.statusLabels.cancelled',
};

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  PAYMENT_PENDING: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  PAID: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  DISPATCHED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  DELIVERED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const ALL_STATUSES: CourierDeliveryStatus[] = [
  'REQUESTED',
  'PAYMENT_PENDING',
  'PAID',
  'DISPATCHED',
  'DELIVERED',
  'CANCELLED',
];

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('hu-HU');
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function CourierDeliveryListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<CourierDeliveryStatus | undefined>(undefined);
  const { deliveries, isLoading, error, refetch } = useCourierDeliveries(undefined, statusFilter);
  const handoffMutation = useHandoffToCourier();
  const deliveredMutation = useMarkDelivered();

  const handleHandoff = async (id: string) => {
    const provider = prompt(t('courier.enterProvider'));
    if (!provider) return;
    const trackingNumber = prompt(t('courier.enterTracking')) ?? undefined;
    await handoffMutation.mutateAsync({ id, data: { courierProvider: provider, trackingNumber } });
    refetch();
  };

  const handleDelivered = async (id: string) => {
    await deliveredMutation.mutateAsync(id);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('courier.title')}</h1>
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('courier.title')}</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-500">{t('service.errorPrefix', { message: error })}</p>
            <Button className="mt-4" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('courier.title')}</h1>
        <Button onClick={() => navigate('/worksheet/courier/new')}>
          {t('courier.newDelivery')}
        </Button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <Button
          variant={statusFilter === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter(undefined)}
        >
          {t('common.all')}
        </Button>
        {ALL_STATUSES.map(s => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(s)}
          >
            {STATUS_LABEL_KEYS[s] ? t(STATUS_LABEL_KEYS[s]) : s}
          </Button>
        ))}
      </div>

      {/* Deliveries list */}
      {deliveries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t('courier.noDeliveries')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deliveries.map(delivery => (
            <Card key={delivery.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[delivery.status] ?? ''}`}
                      >
                        {STATUS_LABEL_KEYS[delivery.status]
                          ? t(STATUS_LABEL_KEYS[delivery.status])
                          : delivery.status}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        #{delivery.id.slice(0, 8)}
                      </span>
                    </div>
                    <div className="flex gap-6 text-sm">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t('courier.worksheetIdLabel')}: {delivery.worksheetId.slice(0, 8)}...
                      </span>
                      <span className="text-gray-600 dark:text-gray-300">{delivery.address}</span>
                      {delivery.courierProvider && (
                        <span className="text-muted-foreground">
                          {t('courier.providerLabel')}: {delivery.courierProvider}
                        </span>
                      )}
                      {delivery.trackingNumber && (
                        <span className="text-muted-foreground">
                          {t('courier.trackingLabel')}: {delivery.trackingNumber}
                        </span>
                      )}
                      {delivery.courierCost != null && (
                        <span className="font-medium">{formatCurrency(delivery.courierCost)}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDate(delivery.createdAt)}
                      {delivery.scheduledDate && (
                        <>
                          {' '}
                          | {t('courier.scheduledLabel')}: {formatDate(delivery.scheduledDate)}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/worksheet/courier/${delivery.id}`)}
                    >
                      {t('common.details')}
                    </Button>
                    {delivery.status === 'PAID' && (
                      <Button
                        size="sm"
                        onClick={() => handleHandoff(delivery.id)}
                        disabled={handoffMutation.isPending}
                      >
                        {t('courier.handoff')}
                      </Button>
                    )}
                    {delivery.status === 'DISPATCHED' && (
                      <Button
                        size="sm"
                        onClick={() => handleDelivered(delivery.id)}
                        disabled={deliveredMutation.isPending}
                      >
                        {t('courier.markDelivered')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
